import { apiClient } from '@/shared/api/client'
import type { ApiResource } from '@/shared/types/api'
import type { AuthPayload, User } from '@/shared/types/domain'

interface LoginInput {
  login: string
  password: string
}

interface RegisterInput {
  name: string
  phone: string
  verification_token: string
  password: string
  password_confirmation: string
  ref?: string
  invitation_token?: string
}

export interface SendCodePayload {
  expires_in: number
  resend_after: number
}

export async function login(input: LoginInput): Promise<AuthPayload> {
  const response = await apiClient.post<ApiResource<AuthPayload>>('/auth/login', input)
  return response.data.data
}

export async function register(input: RegisterInput): Promise<AuthPayload> {
  // withCredentials — чтобы cookie `ref` (лендинг, .ai-manager.website) дошла до API и сработала атрибуция.
  const response = await apiClient.post<ApiResource<AuthPayload>>('/auth/register', input, {
    withCredentials: true,
  })
  return response.data.data
}

export async function sendVerificationCode(phone: string): Promise<SendCodePayload> {
  const response = await apiClient.post<ApiResource<SendCodePayload>>('/auth/phone/send-code', {
    phone,
  })
  return response.data.data
}

export async function verifyPhoneCode(phone: string, code: string): Promise<string> {
  const response = await apiClient.post<ApiResource<{ verification_token: string }>>(
    '/auth/phone/verify-code',
    { phone, code },
  )
  return response.data.data.verification_token
}

export async function me(): Promise<User> {
  const response = await apiClient.get<ApiResource<User>>('/me')
  return response.data.data
}

export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout')
}

export interface ForgotPasswordPayload {
  message: string
  channel?: 'sms' | 'email'
  expires_in?: number
  resend_after?: number
}

export async function forgotPassword(
  login: string,
  context: 'web' | 'admin' = 'web',
): Promise<ForgotPasswordPayload> {
  const response = await apiClient.post<ApiResource<ForgotPasswordPayload>>(
    '/auth/password/forgot',
    { login, context },
  )
  return response.data.data
}

export async function verifyPasswordResetCode(login: string, code: string): Promise<string> {
  const response = await apiClient.post<ApiResource<{ reset_token: string }>>(
    '/auth/password/verify-code',
    { login, code },
  )
  return response.data.data.reset_token
}

export async function resetPasswordViaSms(input: {
  login: string
  reset_token: string
  password: string
  password_confirmation: string
}): Promise<void> {
  await apiClient.post('/auth/password/reset', input)
}

export async function resetPasswordViaEmail(input: {
  email: string
  token: string
  password: string
  password_confirmation: string
}): Promise<void> {
  await apiClient.post('/auth/password/reset', input)
}
